import React, { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import centerOfMass from "@turf/center-of-mass";

import TerritoryLevel from "@mapmycustomers/shared/enum/TerritoryLevel";
import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import { GeoPoint } from "@mapmycustomers/shared/types/shapes";
import AreaRegion from "@mapmycustomers/shared/types/territory/AreaRegion";
import AreaType from "@mapmycustomers/shared/types/territory/AreaType";
import LookupData from "@mapmycustomers/shared/types/territory/LookupData";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import { Feature } from "@app/component/map/FeaturedMap";
import { getDemographicData } from "@app/scene/map/store/layers/boundaries/selectors";
import { RegionDemographicData } from "@app/scene/map/store/layers/boundaries/types/BoundariesLayerData";
import getLevelData from "@app/scene/map/utils/getLevelData";
import { RootState } from "@app/store/rootReducer";
import colors from "@app/styles/_colors.module.scss";
import anyColorToHex from "@app/util/colors/anyColorToHex";
import defaultIdGetter from "@app/util/defaultIdGetter";
import loggingService from "@app/util/logging";
import { AREA_REGION_ZINDEX } from "@app/util/map/consts";

import RegionTooltip from "./components/RegionTooltip";
import areaRegionGeometryGetter from "./utils/areaRegionGeometryGetter";
import getCursor from "./utils/getCursor";
import useContinuousSelectionMode from "./utils/useContinuousSelectionMode";

// we create this AreaRegionItem extension to enforce re-rendering of regions
// when their selection of hover state changes
// Before this, regionStyleGetter which is defined as dynamicCallback wasn't
// able to track this sometimes. Or, to be more precise: it was able, but
// google maps were not calling it because they thought some region's state
// was not changed.
interface AreaRegionItem extends AreaRegion {
  adding: boolean;
  hovered: boolean;
  removing: boolean;
  selected: boolean;
}

const isDifferent = (a: AreaRegionItem, b: AreaRegionItem) =>
  a.id !== b.id ||
  a.hovered !== b.hovered ||
  a.selected !== b.selected ||
  a.adding !== b.adding ||
  a.removing !== b.removing;

interface Props {
  addingRegionIds?: AreaRegion["id"][];
  areaType: AreaType;
  baseMapIsDark?: boolean;
  currentLevel: TerritoryLevel;
  demographicData?: RegionDemographicData;
  fullySelectedCurrentRegionIds?: AreaRegion["id"][]; // region ids fully selected on current level. Matches selectedRegionIds when currentLevel === areaType.level
  hoverColor?: AnyColor;
  lookupData: LookupData;
  onClick?: (entity: AreaRegion, shouldAdd?: boolean) => void;
  onDoubleClick?: (entity: AreaRegion) => void;
  regions: AreaRegion[];
  removingRegionIds?: AreaRegion["id"][];
  selectedCurrentRegionIds?: AreaRegion["id"][]; // region ids selected on current level. Matches selectedRegionIds when currentLevel === areaType.level
  selectedRegionIds?: AreaRegion["id"][];
  styleGetter?: (item: AreaRegion) => google.maps.Data.StyleOptions;
  type?: string;
  zIndex?: number;
}

const AreaRegionFeature: React.FC<Props> = ({
  addingRegionIds,
  areaType,
  baseMapIsDark,
  currentLevel,
  demographicData,
  fullySelectedCurrentRegionIds = [],
  hoverColor = colors.primary100,
  lookupData,
  onClick,
  onDoubleClick,
  regions,
  removingRegionIds,
  selectedCurrentRegionIds = [],
  selectedRegionIds = [],
  styleGetter,
  type = "areaRegions",
  zIndex = AREA_REGION_ZINDEX,
}) => {
  const [hoveredRegion, setHoveredRegion] = useState<AreaRegion | undefined>();
  const [tooltipPosition, setTooltipPosition] = useState<google.maps.LatLngLiteral | undefined>();

  const massCentersCache = useRef<Record<AreaRegion["id"], GeoPoint>>({});
  useEffect(() => {
    massCentersCache.current = {};

    regions.forEach((region) => {
      const centerOfMassPoint = centerOfMass(region.geometry);
      massCentersCache.current[region.id] = centerOfMassPoint.geometry as GeoPoint;
    });
  }, [regions]);

  const continuousSelectionMode = useContinuousSelectionMode();

  const handleClick = useCallback(
    (region: AreaRegion, event: google.maps.MapMouseEvent) => {
      loggingService.debug("region clicked", { event, region });
      onClick?.(region);
    },
    [onClick]
  );

  const handleDoubleClick = useCallback(
    (region: AreaRegion, event: google.maps.MapMouseEvent) => {
      loggingService.debug("region double clicked", { event, region });
      onDoubleClick?.(region);
    },
    [onDoubleClick]
  );

  const handleMouseMove = useDynamicCallback(
    (region: AreaRegion, event: google.maps.MapMouseEvent) => {
      setTooltipPosition(event.latLng?.toJSON());
    }
  );

  const handleMouseOver = useDynamicCallback((region: AreaRegion) => {
    setHoveredRegion(region);
    if (continuousSelectionMode !== "none") {
      if (continuousSelectionMode === "toggle") {
        onClick?.(region);
      } else {
        if (
          (continuousSelectionMode === "add" &&
            !fullySelectedCurrentRegionIds.includes(region.id)) ||
          (continuousSelectionMode === "remove" && selectedCurrentRegionIds.includes(region.id))
        ) {
          onClick?.(region, continuousSelectionMode === "add");
        }
      }
    }
  });

  // special treatment to include currently hovered region into continuous selection
  const handleMouseDown = useDynamicCallback(
    (region: AreaRegion, event: google.maps.MapMouseEvent) => {
      const { altKey, shiftKey } = event.domEvent as unknown as MouseEvent;
      // we calculate continuousSelectionMode here because continuousSelectionMode from a hook
      // is not yet updated at this point
      const continuousSelectionMode =
        altKey || shiftKey ? (altKey && shiftKey ? "toggle" : altKey ? "remove" : "add") : "none";

      if (continuousSelectionMode !== "none") {
        if (continuousSelectionMode === "toggle") {
          onClick?.(region);
        } else {
          if (
            (continuousSelectionMode === "add" &&
              !fullySelectedCurrentRegionIds.includes(region.id)) ||
            (continuousSelectionMode === "remove" && selectedCurrentRegionIds.includes(region.id))
          ) {
            onClick?.(region, continuousSelectionMode === "add");
          }
        }
      }
    }
  );

  const handleMouseLeave = useCallback((region: AreaRegion) => {
    setHoveredRegion((hoveredRegion) =>
      hoveredRegion?.id === region.id ? undefined : hoveredRegion
    );
  }, []);

  const regionStyleGetter = useDynamicCallback((region: AreaRegionItem) => {
    const { adding, hovered, removing, selected } = region;
    const strokeColor = baseMapIsDark ? colors.white : colors.slate;
    return {
      clickable: true,
      cursor: hovered ? getCursor(continuousSelectionMode) : undefined,
      draggable: false,
      fillColor: adding
        ? anyColorToHex(hoverColor)
        : selected || removing
        ? colors.accentGray
        : hovered
        ? anyColorToHex(hoverColor)
        : colors.accentGray,
      fillOpacity:
        adding || removing
          ? hovered
            ? 0.9
            : 0
          : selected
          ? hovered
            ? 0.8
            : 0
          : hovered
          ? 0.8
          : 0,
      strokeColor: selected ? strokeColor : hovered ? anyColorToHex(hoverColor) : strokeColor,
      strokeOpacity: selected ? (hovered ? 1 : 0) : 1,
      strokeWeight: adding || removing ? 0 : hovered ? 4 : 2,
      zIndex: zIndex + (hovered || adding || removing ? 10 : 0), // hovered and in loading state are slightly above
    };
  });

  const regionFeatures = useMemo(() => {
    const selectedRegionIdsSet = new Set(selectedRegionIds);
    const addingRegionIdsSet = new Set(addingRegionIds);
    const removingRegionIdsSet = new Set(removingRegionIds);
    return regions.map<AreaRegionItem>((region) => ({
      ...region,
      adding: addingRegionIdsSet.has(region.id),
      hovered: hoveredRegion?.id === region.id,
      removing: removingRegionIdsSet.has(region.id),
      selected: selectedRegionIdsSet.has(region.id),
    }));
  }, [hoveredRegion, selectedRegionIds, addingRegionIds, removingRegionIds, regions]);

  let regionDemographyData = undefined;
  if (hoveredRegion && demographicData) {
    regionDemographyData = getLevelData(demographicData, currentLevel)[hoveredRegion.id];
  }

  return (
    <>
      <Feature<AreaRegionItem>
        geometryGetter={areaRegionGeometryGetter}
        idGetter={defaultIdGetter}
        isDifferent={isDifferent}
        items={regionFeatures}
        key={baseMapIsDark ? "dark" : "bright"}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        onMouseOver={handleMouseOver}
        styleGetter={styleGetter ?? regionStyleGetter}
        type={type}
      />
      <RegionTooltip
        areaType={areaType}
        continuousSelectionMode={continuousSelectionMode}
        currentLevel={currentLevel}
        lookupData={lookupData}
        position={tooltipPosition}
        region={hoveredRegion}
        regionDemographyData={regionDemographyData}
        selectedRegionIds={selectedRegionIds}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  demographicData: getDemographicData(state),
});

export default connect(mapStateToProps)(AreaRegionFeature);
